import React, { useState, useEffect, useRef } from "react";
import styles from "../styles/chatComp.module.css";
import TravelCard from "./componetsChat/comp1";
import LocationComp from "./componetsChat/comp2";
import TrainCard from "./componetsChat/comp3";
import FlightCard from "./componetsChat/comp4";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import RouteMapModal from "./modal/RouteMapModal";
import ShareMapModal from "./modal/ShareMapModal";

const ChatComp = ({
  data,
  showMoreData,
  handleShowMore,
  isPaginating,
  selectedTravelOption,
  onTravelOptionSelect,
}) => {


  const [selectedTimelineSteps, setSelectedTimelineSteps] = useState([]);
  const [selected, setSelected] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const containerRef = useRef(null);
  const messageContainerRef = useRef(null);
  const showMoreRef = useRef(null);
  const scrollPositionRef = useRef(null);
  const navigate = useNavigate();

  const [mapModalState, setMapModalState] = useState({
    isOpen: false,
    route: null,
    title: "",
    searchMetadata: null,
  });

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareData, setShareData] = useState({ route: null, metadata: null });

  const handleViewMap = (route, title, searchMetadata = null) => {
    setMapModalState({
      isOpen: true,
      route,
      title,
      searchMetadata,
    });
  };

  const handleShareRoute = (route, departure, arrival, item = {}) => {
    // Search aggressively for the best (fullest) name available
    const findBestName = (primary, secondary, routeNodeName, fallback) => {
      // Prefer standardized names from item or route object first
      if (primary && primary.length > 5) return primary;
      if (secondary && secondary.length > 5) return secondary;
      // Then prefer the full station name from the first/last node
      if (routeNodeName && routeNodeName.length > 3) return routeNodeName;
      // Finally fall back to whatever is available
      return primary || secondary || routeNodeName || fallback;
    };

    const srcName = findBestName(
      item.user_src_name,
      route?.user_src_name,
      route?.nodes?.[0]?.segment?.origin_name,
      departure.name || departure.location
    );

    const destName = findBestName(
      item.user_dest_name,
      route?.user_dest_name,
      route?.nodes?.[route?.nodes?.length - 1]?.segment?.dest_name,
      arrival.name || arrival.location
    );

    const metadata = {
      user_src_name: srcName,
      user_dest_name: destName,
      user_src_ll: route?.user_src_ll || [departure?.lat, departure?.lng],
      user_dest_ll: route?.user_dest_ll || [arrival?.lat, arrival?.lng],
      date: departure.date_raw || departure.date || "",
      fallback_url: route?.search_url || item.fallback_url || window.location.href // Prioritize search_url for chat results
    };
    setShareData({ route, metadata });
    setIsShareModalOpen(true);
  };

  const closeMapModal = () => {
    setMapModalState((prev) => ({ ...prev, isOpen: false }));
  };

  // Update selected timeline steps when data or selection changes
  useEffect(() => {
    if (!data || data.length === 0) {
      setSelectedTimelineSteps([]);
      setSelectedItem(null);
      return;
    }
    // console.log("> Travel Card Data: ", data);
    if (selectedTravelOption) {
      // Find the index of the selected item
      const selectedIndex = data.findIndex(
        (item) =>
          item.departure?.time === selectedTravelOption.departure?.time &&
          item.arrival?.time === selectedTravelOption.arrival?.time &&
          item.price === selectedTravelOption.price
      );

      if (selectedIndex !== -1) {
        // Update timeline steps for the selected item (handle both camelCase and snake_case keys)
        setSelectedTimelineSteps(data[selectedIndex].timelineSteps || data[selectedIndex].timeline_steps || []);
        setSelectedItem(data[selectedIndex]);
      } else {
        // If selected item not found, reset to first item
        setSelectedTimelineSteps(data[0].timelineSteps || data[0].timeline_steps || []);
        setSelectedItem(data[0]);
        onTravelOptionSelect(data[0]);
      }
    } else {
      // If no selection, default to first item
      setSelectedTimelineSteps(data[0].timelineSteps || data[0].timeline_steps || []);
      setSelectedItem(data[0]);
      onTravelOptionSelect(data[0]);
    }

    for (let i = 0; i < data.length; i++) {
      if (i == selected) {
        setSelectedTimelineSteps(data[i].timelineSteps);
        setSelectedItem(data[i]);
        break;
      }
    }
  }, [data, selectedTravelOption, onTravelOptionSelect, selected]);

  // Handle travel card click
  const handleTravelCardClick = (item, index) => {
    setSelectedTimelineSteps(item.timelineSteps);
    setSelectedItem(item);
    setSelected(index);
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Handle flight booking
  const handleBookFlight = async () => {
    // Get Firebase user token
    const auth = getAuth();
    const user = auth.currentUser;
    let userToken = null;

    if (user) {
      try {
        userToken = await user.getIdToken();
      } catch (error) {
        console.error("Error getting user token:", error);
      }
    } else {
      console.warn("No user logged in - proceeding without token");
    }

    // Save source and query to localStorage for persistence across reloads/logouts
    localStorage.setItem("booking_source", "chat");
    localStorage.setItem("booking_search_query", "");

    // Find flight data from timeline steps
    const flightStep = selectedTimelineSteps?.find(
      (step) => step.type === "FlightCard"
    );


    // Extract booking parameters
    const trace_id = flightStep?.data?.trace_id || selectedItem?.trace_id;
    const result_idx = flightStep?.data?.result_idx || selectedItem?.result_idx;
    const is_lcc = flightStep?.data?.is_lcc || selectedItem?.is_lcc;
    const valid_till_ts =
      flightStep?.data?.valid_till_ts || selectedItem?.valid_till_ts;

    // Build URL with query parameters
    const params = new URLSearchParams();
    if (trace_id) params.append("trace_id", trace_id);
    if (result_idx !== undefined && result_idx !== null)
      params.append("result_idx", result_idx);
    if (is_lcc !== undefined && is_lcc !== null)
      params.append("is_lcc", is_lcc);
    if (valid_till_ts !== undefined && valid_till_ts !== null)
      params.append("valid_till_ts", valid_till_ts);

    const url = `/book-flight?${params.toString()}`;

    // // Navigate with both URL params and location state
    navigate(url, {
      state: {
        trace_id: trace_id,
        result_idx: result_idx,
        is_lcc: is_lcc,
        valid_till_ts: valid_till_ts,
        baggage: flightStep?.data?.baggage || selectedItem?.baggage,
        userToken: userToken,
        // Optional: Pass additional flight details for display
        flightDetails: {
          departure: selectedItem?.departure,
          arrival: selectedItem?.arrival,
          duration: selectedItem?.duration,
          stops: selectedItem?.stops,
          price: selectedItem?.price,
          currency: selectedItem?.currency,
          tag: selectedItem?.tag,
          modes: selectedItem?.modes,
        },
      },
    });
  };

  // Handle show more click
  const handleShowMoreClick = (key, value) => {
    // Store current scroll position
    const chatHistory = document.querySelector(".chat-history");
    if (chatHistory) {
      scrollPositionRef.current = chatHistory.scrollTop;
    }

    handleShowMore(key, value);

    // Scroll to the bottom of this message container after a short delay
    setTimeout(() => {
      if (showMoreRef.current && chatHistory) {
        const messageBottom =
          showMoreRef.current.getBoundingClientRect().bottom;
        const chatHistoryTop = chatHistory.getBoundingClientRect().top;
        const relativeBottom = messageBottom - chatHistoryTop;

        // Calculate new scroll position
        const newScrollTop =
          chatHistory.scrollTop +
          relativeBottom -
          chatHistory.clientHeight +
          50;

        // Scroll to new position
        chatHistory.scrollTo({
          top: newScrollTop,
          behavior: "smooth",
        });

        // Prevent chat from scrolling to end
        const preventScroll = () => {
          if (chatHistory.scrollTop > newScrollTop + 100) {
            chatHistory.scrollTop = newScrollTop;
          }
        };

        // Add scroll event listener
        chatHistory.addEventListener("scroll", preventScroll);

        // Remove scroll event listener after animation completes
        setTimeout(() => {
          chatHistory.removeEventListener("scroll", preventScroll);
        }, 1000);
      }
    }, 100);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setSelectedTimelineSteps([]);
    };
  }, []);

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className={styles["main-content"]} ref={containerRef}>
      <div className={styles["container1"]} ref={messageContainerRef}>
        <div className={styles["card-wrapper"]}>
          {data.map((item, index) => {

            const isSelected = index == selected;
            const uniqueKey = `travel-${index}-${item.trace_id || "na"}-${item.result_idx || "na"
              }-${item.departure?.time || ""}-${item.arrival?.time || ""}-${item.price || ""
              }`;

            // Debug: Verify all flight data is present
            // if (index === 0) {
            //   console.log('First TravelCard data:', {
            //     trace_id: item.trace_id,
            //     result_idx: item.result_idx,
            //     is_lcc: item.is_lcc,
            //     valid_till_ts: item.valid_till_ts,
            //     baggage: item.baggage
            //   });
            // }

            return (
              <div
                key={uniqueKey}
                className={`${styles["card-container-wrapper"]} ${isSelected ? styles["selected"] : ""}`}
                onClick={() => handleTravelCardClick(item, index)}
              >
                <TravelCard
                  tag={item.tag}
                  departure={item.departure}
                  arrival={item.arrival}
                  duration={item.duration}
                  stops={item.stops}
                  price={item.price}
                  currency={item.currency}
                  availability={item.availability}
                  modes={item.modes}
                  toolTip={item.toolTip}
                  transportModes={item.transportModes}
                  isSelected={isSelected}
                  trace_id={item.trace_id}
                  result_idx={item.result_idx}
                  is_lcc={item.is_lcc}
                  valid_till_ts={item.valid_till_ts}
                  baggage={item.baggage}
                  flatOffAmount={item.flat_off_amount_flight || item.originalRoute?.flat_off_amount_flight}
                  flatOffPromo={item.flat_off_flight_promo || item.originalRoute?.flat_off_flight_promo}
                  flatOffPromoDesc={item.flat_off_flight_promo_desc || item.originalRoute?.flat_off_flight_promo_desc}
                  onViewMap={() => {
                    // Search aggressively for the best (fullest) name available
                    const findBestName = (primary, secondary, routeNodeName, fallback) => {
                      if (primary && primary.length > 5) return primary;
                      if (secondary && secondary.length > 5) return secondary;
                      if (routeNodeName && routeNodeName.length > 3) return routeNodeName;
                      return primary || secondary || routeNodeName || fallback;
                    };

                    const src = findBestName(
                      item.user_src_name,
                      item.originalRoute?.user_src_name,
                      item.originalRoute?.nodes?.[0]?.segment?.origin_name,
                      item.departure.name || item.departure.location
                    );

                    const dest = findBestName(
                      item.user_dest_name,
                      item.originalRoute?.user_dest_name,
                      item.originalRoute?.nodes?.[item.originalRoute?.nodes?.length - 1]?.segment?.dest_name,
                      item.arrival.name || item.arrival.location
                    );

                    const cardTitle = `Route: ${src} to ${dest}`;
                    const metadata = {
                      user_src_name: src,
                      user_dest_name: dest,
                      user_src_ll: item.originalRoute?.user_src_ll || [item.departure?.lat, item.departure?.lng],
                      user_dest_ll: item.originalRoute?.user_dest_ll || [item.arrival?.lat, item.arrival?.lng],
                      date: item.departure.date_raw || item.departure.date || "",
                      fallback_url: item.originalRoute?.search_url || item.fallback_url || window.location.href // Prioritize search_url
                    };
                    // Pass null as title to let RouteMapModal calculate its professional title (with codes)
                    handleViewMap(item.originalRoute, null, metadata);
                  }}
                  onShare={() => handleShareRoute(item.originalRoute, item.departure, item.arrival, item)}
                />
              </div>
            );
          })}
        </div>

        {showMoreData && (
          <div className={styles["show-more-container"]} ref={showMoreRef} style={{ display: "flex", justifyContent: "center", padding: "8px 0", flexShrink: 0 }}>
            <Button
              onClick={() =>
                handleShowMoreClick(showMoreData.key, showMoreData.value)
              }
              disabled={isPaginating}
              className={styles["show-more-btn"]}
            >
              {isPaginating ? (
                <>
                  <svg
                    className={styles["refresh-icon-spinning"]}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <svg
                    className={styles["refresh-icon"]}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  <span>Show more</span>
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <div className={styles["container2"]}>
        {/* Orange Summary Header */}
        {selectedItem && (
          <div className={styles["summary-header"]}>
            <div className={styles["summary-content"]}>
              <div className={styles["info-groups"]}>
                <div className={styles["duration-group"]}>
                  <span className={styles["duration-label"]}>Total Duration</span>
                  <span className={styles["duration-value"]}>
                    {selectedItem.duration || "N/A"}
                  </span>
                </div>

                <div className={styles["price-group"]}>
                  <span className={styles["price-label"]}>Total Price</span>
                  <span className={styles["price-value"]}>
                    {selectedItem.currency || "₹"}
                    {selectedItem.price || "N/A"}
                  </span>
                </div>
              </div>

              <div className={styles["action-buttons"]}>
                {/* Show IRCTC button if train is in the modes */}
                {(selectedItem.modes?.includes("train") || selectedTimelineSteps?.some(s => s.type === "TrainCard")) &&
                  selectedTimelineSteps?.some(
                    (step) => step.type === "TrainCard" && step.data?.bookUrl
                  ) && (
                    <a
                      href={
                        selectedTimelineSteps.find(
                          (step) => step.type === "TrainCard"
                        )?.data?.bookUrl
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles["book-button"]}
                    >
                      Book with IRCTC
                    </a>
                  )}
                {/* Show Book Flight button if flight is in the modes */}
                {(selectedItem.modes?.includes("flight") || selectedTimelineSteps?.some(s => s.type === "FlightCard")) && (
                  <button
                    onClick={handleBookFlight}
                    className={styles["book-button"]}
                  >
                    Book Flight
                  </button>
                )}
                {/* Show Book Bus button if bus is in the modes */}
                {(selectedItem.modes?.includes("bus") || selectedTimelineSteps?.some(s => s.type === "BusCard")) &&
                  selectedTimelineSteps?.some(
                    (step) => step.type === "BusCard" && step.data?.bookUrl
                  ) && (
                    <a
                      href={
                        selectedTimelineSteps.find(
                          (step) => step.type === "BusCard"
                        )?.data?.bookUrl
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles["book-button"]}
                    >
                      Book Bus
                    </a>
                  )}
                {/* Share button */}
                <button
                  className={styles["share-button"]}
                  onClick={() => handleShareRoute(selectedItem.originalRoute, selectedItem.departure, selectedItem.arrival, selectedItem)}
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles["timeline-list"]}>
          {selectedTimelineSteps?.map((step, idx) => {
            const stepKey = `timeline-${idx}-${step.type}-${step.data?.trainName || step.data?.name || ""
              }-${step.data?.from?.time || ""}-${step.data?.to?.time || ""}`;
            return (
              <div
                className={`${styles["timeline-step"]} ${idx === 0
                  ? styles["first"]
                  : idx === selectedTimelineSteps.length - 1
                    ? styles["last"]
                    : ""
                  }`}
                key={stepKey}
              >
                <div
                  className={
                    styles["timeline-icon-col"] +
                    (selectedTimelineSteps.length === 1
                      ? ` ${styles["single-component"]}`
                      : "")
                  }
                >
                  <div className={styles["timeline-icon"]}>{step.icon}</div>
                </div>
                <div className={styles["timeline-content"]}>
                  {step.type === "TrainCard" && <TrainCard {...step.data} />}
                  {step.type === "BusCard" && <TrainCard {...step.data} />}
                  {step.type === "LocationComp" && (
                    <LocationComp {...step.data} />
                  )}

                  {step.type === "FlightCard" && (
                    <>
                      <FlightCard {...step.data} flatOffAmount={selectedItem?.flat_off_amount_flight || selectedItem?.originalRoute?.flat_off_amount_flight} flatOffPromo={selectedItem?.flat_off_flight_promo || selectedItem?.originalRoute?.flat_off_flight_promo} flatOffPromoDesc={selectedItem?.flat_off_flight_promo_desc || selectedItem?.originalRoute?.flat_off_flight_promo_desc} />
                      <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 0 8px 0" }}>
                        <button
                          onClick={handleBookFlight}
                          style={{
                            padding: "7px 18px",
                            borderRadius: "8px",
                            border: "2px solid #FF4E01",
                            color: "#FF4E01",
                            fontWeight: "700",
                            fontSize: "14px",
                            background: "transparent",
                            cursor: "pointer",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "#FF4E01"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#FF4E01"; }}
                        >
                          Book Flight
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <RouteMapModal
        isOpen={mapModalState.isOpen}
        onClose={closeMapModal}
        routeData={mapModalState.route}
        title={mapModalState.title}
        searchMetadata={mapModalState.searchMetadata}
      />

      <ShareMapModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        routeData={shareData.route}
        searchMetadata={shareData.metadata}
      />
    </div>
  );
};

export default ChatComp;
